import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import { 
  FileJson, RefreshCw, Eye, Download, Trash2, Upload,
  ChevronLeft, ChevronRight, Zap, ExternalLink,
  CheckCircle, AlertCircle, Clock, X, File
} from 'lucide-react';
import { apiCall, v4Extract } from '../api';
import '../components/V1WorkOrdersList.css';

// Model config for V4
const MODEL_CONFIG = {
  name: "XOrbit V4",
  description: "AWS Textract OCR + Qwen Mapping",
  color: "#059669",
  bgColor: "#d1fae5",
};

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="v1-custom-dropdown" ref={dropdownRef}>
      <button className="v1-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="v1-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`v1-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || 
      f.name.toLowerCase().endsWith('.tiff') || 
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg') ||
      f.name.toLowerCase().endsWith('.png')
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, and image files are supported.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    
    const newStatus = {};
    uniqueFiles.forEach(f => {
      newStatus[f.name] = 'pending';
    });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const result = await v4Extract(file);
        setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
        results.push({ filename: file.name, success: true, work_order_id: result.work_order_id });
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success':
        return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error':
        return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default:
        return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="v1-modal-overlay" onClick={handleClose}>
      <div className="v1-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="v1-modal-header" style={{ borderBottomColor: MODEL_CONFIG.color }}>
          <h3>
            <Upload size={20} style={{ color: MODEL_CONFIG.color }} />
            Upload Documents - {MODEL_CONFIG.name}
          </h3>
          <button className="v1-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="v1-modal-body">
          <div 
            ref={dropZoneRef}
            className="v1-drop-zone"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload size={40} style={{ color: MODEL_CONFIG.color }} />
            <p>Drop files here or click to browse</p>
            <span>PDF, TIFF, JPG, PNG</span>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {files.length > 0 && (
            <div className="v1-file-list">
              <div className="v1-file-list-header">
                <span>{files.length} file(s) selected</span>
              </div>
              <div className="v1-file-items">
                {files.map(file => (
                  <div key={file.name} className={`v1-file-item ${uploadStatus[file.name]}`}>
                    <div className="v1-file-info">
                      {getStatusIcon(uploadStatus[file.name])}
                      <File size={16} />
                      <span className="v1-file-name">{file.name}</span>
                      <span className="v1-file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                    {uploadStatus[file.name] === 'pending' && (
                      <button className="v1-file-remove" onClick={() => removeFile(file.name)}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {allUploaded && (
            <div className="v1-upload-summary" style={{ marginTop: '1rem', color: '#22c55e' }}>
              <CheckCircle size={16} style={{ marginRight: '0.5rem' }} />
              Upload complete: {successCount} successful, {errorCount} failed
            </div>
          )}
        </div>

        <div className="v1-modal-footer">
          <button className="v1-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button 
              className="v1-btn-upload-all"
              style={{ backgroundColor: MODEL_CONFIG.color }}
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  Extract All ({files.length})
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main V4 Work Orders Component
const V4WorkOrders = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 20, skip: 0
  });
  const [stats, setStats] = useState({
    total: 0, completed: 0, processing: 0, pending: 0, failed: 0
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchWorkOrders = useCallback(async (skip = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `/v1/v4/work-orders?skip=${skip}&limit=${pagination.limit}`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch work orders');
      }
      
      const data = await response.json();
      const workOrdersList = data.data || [];
      setWorkOrders(workOrdersList);
      setPagination(data.pagination || { total: 0, page: 1, pages: 1, limit: 20, skip: 0 });
      
      // Calculate stats
      const completed = workOrdersList.filter(wo => wo.status === 'completed').length;
      const processing = workOrdersList.filter(wo => wo.status === 'processing').length;
      const pending = workOrdersList.filter(wo => wo.status === 'pending' || wo.status === 'created').length;
      const failed = workOrdersList.filter(wo => wo.status === 'failed').length;
      
      setStats({
        total: data.pagination?.total || workOrdersList.length,
        completed,
        processing,
        pending,
        failed,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, statusFilter]);

  useEffect(() => {
    fetchWorkOrders(0);
  }, [statusFilter]);

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchWorkOrders(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchWorkOrders(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const handleViewWorkOrder = (workOrderId) => {
    navigate(`/v1/work-order/${workOrderId}`);
  };

  const handleDeleteWorkOrder = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this work order?')) {
      return;
    }
    
    try {
      const response = await fetch(`/v1/v4/work-orders/${workOrderId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete work order');
      }
      
      fetchWorkOrders(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleDownloadFile = async (workOrderId, filename, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/v1/v4/work-orders/${workOrderId}/file`);
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleOpenV4Docs = () => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:5001'
      : `${window.location.protocol}//${window.location.hostname}`;
    // V4 docs are public - no uid needed
    const v4DocsUrl = `${baseUrl}/api/extraction/xorbit/v4/docs`;
    window.open(v4DocsUrl, '_blank');
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    const term = searchTerm.toLowerCase();
    return (
      wo._id?.toLowerCase().includes(term) ||
      wo.filename?.toLowerCase().includes(term) ||
      wo.uid?.toLowerCase().includes(term) ||
      wo.trace_id?.toLowerCase().includes(term)
    );
  });

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      created: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle size={12} />;
      case 'processing': return <RefreshCw size={12} className="spin" />;
      case 'failed': return <AlertCircle size={12} />;
      default: return <Clock size={12} />;
    }
  };

  return (
    <div className="v1-work-orders-list">
      {/* Header with Model Info */}
      <div className="v1-header-banner" style={{ borderLeftColor: MODEL_CONFIG.color }}>
        <div className="v1-header-info">
          <h2 style={{ color: MODEL_CONFIG.color }}>
            <Zap size={24} />
            {MODEL_CONFIG.name} Work Orders
          </h2>
          <p>{MODEL_CONFIG.description} - V4 Extraction Pipeline with Coordinates & Confidence</p>
        </div>
        <div className="v1-header-actions">
          <button
            className="v1-btn-upload"
            onClick={() => setShowUploadModal(true)}
            style={{ backgroundColor: MODEL_CONFIG.color }}
          >
            <Upload size={18} /> Upload & Extract
          </button>
          <button
            className="v1-btn-secondary"
            onClick={handleOpenV4Docs}
            style={{ borderColor: MODEL_CONFIG.color, color: MODEL_CONFIG.color }}
          >
            <ExternalLink size={18} /> API Docs
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={(results) => {
          setShowUploadModal(false);
          fetchWorkOrders(0);
        }}
      />

      {error && (
        <div className="v1-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="v1-stats-grid">
        <div className="v1-stat-card">
          <div className="v1-stat-value">{stats.total}</div>
          <div className="v1-stat-label">Total</div>
        </div>
        <div className="v1-stat-card completed">
          <div className="v1-stat-value">{stats.completed}</div>
          <div className="v1-stat-label">Completed</div>
        </div>
        <div className="v1-stat-card processing">
          <div className="v1-stat-value">{stats.processing}</div>
          <div className="v1-stat-label">Processing</div>
        </div>
        <div className="v1-stat-card pending">
          <div className="v1-stat-value">{stats.pending}</div>
          <div className="v1-stat-label">Pending</div>
        </div>
        <div className="v1-stat-card failed">
          <div className="v1-stat-value">{stats.failed}</div>
          <div className="v1-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="v1-filters-row">
        <input
          type="text"
          className="v1-search-input"
          placeholder="Search by ID, filename, uid, or trace_id..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <CustomStatusDropdown
          value={statusFilter}
          onChange={(val) => setStatusFilter(val)}
        />
        <button className="v1-btn-refresh" onClick={() => fetchWorkOrders(pagination.skip)}>
          <RefreshCw size={16} className={loading ? "spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="v1-table-container">
        <table className="v1-table">
          <thead>
            <tr>
              <th>Work Order ID</th>
              <th>Filename</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Processing Time</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="v1-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading V4 work orders...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td colSpan="7" className="v1-table-empty">
                  No V4 work orders found. Upload a document to get started.
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id} onClick={() => handleViewWorkOrder(wo._id)}>
                  <td className="v1-id-cell">{wo._id?.substring(0, 12)}...</td>
                  <td className="v1-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 35)}{wo.filename?.length > 35 ? '...' : ''}
                  </td>
                  <td>
                    <span className={`v1-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {getStatusIcon(wo.status)}
                      {' '}{wo.status}
                    </span>
                  </td>
                  <td>{wo.total_pages || '-'}</td>
                  <td>{wo.processing_time ? `${wo.processing_time.toFixed(2)}s` : '-'}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td className="v1-actions-cell">
                    <button
                      className="v1-action-btn view"
                      title="View Details"
                      onClick={(e) => { e.stopPropagation(); handleViewWorkOrder(wo._id); }}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="v1-action-btn json"
                      title="Download File"
                      onClick={(e) => handleDownloadFile(wo._id, wo.filename, e)}
                    >
                      <Download size={16} />
                    </button>
                    <button
                      className="v1-action-btn delete"
                      title="Delete"
                      onClick={(e) => handleDeleteWorkOrder(wo._id, e)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="v1-pagination">
          <div className="v1-pagination-info">
            Showing {filteredWorkOrders.length} of {pagination.total} work orders (Page {pagination.page} of {pagination.pages})
          </div>
          <div className="v1-pagination-controls">
            <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
              <ChevronLeft size={16} /> Previous
            </button>
            <span className="v1-page-indicator">Page {pagination.page}</span>
            <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default V4WorkOrders;
